import { useEffect, useRef } from 'react'
import {
  sendBannerImpressionUnique,
  sendBannerImpressionRepeated,
} from 'utils/hooks/analytics/GtmAgent/sendBannerImpression.js'

const isElementFullyInViewport = (el) => {
  if (!el) return false
  const rect = el.getBoundingClientRect()
  return (
    rect.top >= 56 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

// for 輪播類型 Banner
export const useBannerGAForCarousel = ({
  bannerData,
  moduleId,
  bannerLocation,
  venderName,
  venderId,
  swiper,
  isRepeated = false,
  enabled = true,
}) => {
  const sentBanners = useRef(new Set())
  const observerRef = useRef(null)
  const lastSentBanner = useRef(null) // 記錄最後發送 GA 的 banner ID

  const handleImpression = (currentBanner) => {
    if (!currentBanner) return

    // 檢查是否為同一個 banner 避免連續重複發送
    if (lastSentBanner.current === currentBanner._id) {
      return
    }

    if (isRepeated) {
      sendBannerImpressionRepeated({
        banner: currentBanner,
        moduleId,
        bannerLocation,
        venderName,
        venderId,
      })
      lastSentBanner.current = currentBanner._id
    } else if (!sentBanners.current.has(currentBanner._id)) {
      sendBannerImpressionUnique({
        banner: currentBanner,
        moduleId,
        bannerLocation,
        venderName,
        venderId,
      })
      sentBanners.current.add(currentBanner._id)
      lastSentBanner.current = currentBanner._id
    }
  }

  useEffect(() => {
    if (!enabled || !swiper || !bannerData?.length) return

    // 建立 IntersectionObserver 以觀測 swiper 容器
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // 當 swiper 容器完全進入 viewport 時
          if (entry.isIntersecting && entry.intersectionRatio >= 1) {
            const activeIndex = swiper?.realIndex ?? 0
            const currentBanner = bannerData[activeIndex]

            if (currentBanner) {
              handleImpression(currentBanner)
            }
          } else if (entry.intersectionRatio === 0) {
            // 當容器完全離開 viewport 時重置記錄，允許下次進入時再次觸發
            lastSentBanner.current = null
          }
        })
      },
      {
        threshold: [0, 1.0],
        rootMargin: '-56px 0px 0px 0px',
      }
    )

    // 開始觀測 swiper 容器
    if (swiper.el) {
      observerRef.current.observe(swiper.el)
    }

    // 處理 swiper 切換事件
    const handleSlideChange = () => {
      const activeIndex = swiper?.realIndex ?? 0
      const currentBanner = bannerData[activeIndex]
      if (swiper.el && isElementFullyInViewport(swiper.el) && currentBanner) {
        handleImpression(currentBanner)
      }
    }

    // 監聽 swiper 切換事件
    // 輪播效果結束後以及 banner index 改變時皆觸發檢查
    if (swiper) {
      swiper.on('slideChangeTransitionEnd', handleSlideChange)
      swiper.on('realIndexChange', handleSlideChange)
    }

    return () => {
      // 清理 observer
      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      // 清理 swiper 事件監聽
      if (swiper) {
        swiper.off('slideChangeTransitionEnd', handleSlideChange)
        swiper.off('realIndexChange', handleSlideChange)
      }
    }
  }, [
    swiper,
    bannerData,
    isRepeated,
    moduleId,
    bannerLocation,
    venderName,
    venderId,
    enabled,
  ])

  // 初始化完成時若在 viewport 內則馬上紀錄
  useEffect(() => {
    if (enabled && swiper && swiper.initialized && bannerData?.length > 0) {
      if (swiper.el && isElementFullyInViewport(swiper.el)) {
        const activeIndex = swiper?.realIndex ?? 0
        const currentBanner = bannerData[activeIndex]
        if (currentBanner) {
          handleImpression(currentBanner)
        }
      }
    }
  }, [enabled, swiper?.initialized, bannerData])
}

// for 非輪播類型 Banner
export const useBannerGAForNonCarousel = ({
  bannerData,
  moduleId,
  bannerLocation,
  isRepeated = false,
}) => {
  const sentBanners = useRef(new Set())
  const inViewportState = useRef(new Map())
  const observerRef = useRef(null)

  const handleImpression = (banner) => {
    if (!banner?._id) return

    if (isRepeated) {
      if (!inViewportState.current.get(banner._id)) {
        sendBannerImpressionRepeated({
          banner: banner,
          moduleId,
          bannerLocation,
        })
        inViewportState.current.set(banner._id, true)
      }
    } else if (!sentBanners.current.has(banner._id)) {
      sendBannerImpressionUnique({
        banner: banner,
        moduleId,
        bannerLocation,
      })
      sentBanners.current.add(banner._id)
    }
  }

  useEffect(() => {
    const banner = bannerData?.[0]
    if (!banner?._id) return

    // 建立 IntersectionObserver 以觀測 banner
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const bannerId = entry.target.getAttribute('data-banner-id')
          // 當 banner 完全進入 viewport 時
          if (entry.isIntersecting && entry.intersectionRatio >= 1) {
            const targetBanner =
              bannerData.find((b) => b._id === bannerId) || banner

            if (targetBanner) {
              handleImpression(targetBanner)
            }
          } else if (entry.intersectionRatio === 0) {
            // 當 banner 完全離開 viewport 時，重置該 banner 在 viewport 的狀態
            if (bannerId && isRepeated) {
              inViewportState.current.set(bannerId, false)
            }
          }
        })
      },
      {
        threshold: [0, 1.0],
        rootMargin: '-56px 0px 0px 0px',
      }
    )

    // 開始觀察所有具有 data-banner-id 屬性的元素
    const bannerElements = document.querySelectorAll('[data-banner-id]')
    bannerElements.forEach((element) => {
      const bannerId = element.getAttribute('data-banner-id')
      // 只觀察當前 bannerData 中包含的 banner
      if (bannerData.some((b) => b._id === bannerId)) {
        observerRef.current.observe(element)
      }
    })

    return () => {
      // 清理 observer
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [bannerData, isRepeated, moduleId, bannerLocation])
}
